import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export async function generateAnalysisReport(analysisData, activeView, timeLapseData, chartImages = []) {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;

    // Helper function to add a new page if needed
    const checkPageBreak = (neededSpace) => {
      if (yPos + neededSpace > pageHeight - margin) {
        pdf.addPage();
        yPos = margin;
        return true;
      }
      return false;
    };

    // Title
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(41, 128, 185);
    pdf.text('Coloc3D-STL Analysis Report', pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    // Subtitle
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    const timestamp = new Date().toLocaleString();
    pdf.text(`Generated: ${timestamp}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;

    // Analysis Type
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    const analysisType = activeView === '3d' ? '3D Stack Analysis' : 'Time-lapse Analysis';
    pdf.text(analysisType, margin, yPos);
    yPos += 10;

    // Divider line
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;

    if (activeView === '3d' && analysisData) {
      // Stack Info Section
      if (analysisData.stack_info) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(41, 128, 185);
        pdf.text('Stack Information', margin, yPos);
        yPos += 8;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        pdf.text(`Dimensions: ${analysisData.stack_info.shape.join(' x ')} voxels`, margin, yPos);
        yPos += 6;
        pdf.text(`Type: ${analysisData.stack_info.is_timelapse ? 'Time-lapse' : 'Z-stack'}`, margin, yPos);
        yPos += 12;
      }

      // Colocalization Metrics Section
      const metrics = analysisData.metrics;
      if (metrics) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(41, 128, 185);
        pdf.text('Colocalization Metrics', margin, yPos);
        yPos += 8;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Threshold Method: ${(metrics.threshold_method || 'otsu').toUpperCase()}`, margin, yPos);
        yPos += 8;

        // Create metrics table
        const metricsTableData = [
          ['Pearson Correlation (r)', formatNumber(metrics.pearson_r, 4), interpretPearson(metrics.pearson_r)],
          ['Costes\' p-value', formatPValue(metrics.costes_p_value), interpretCostesSignificance(metrics.costes_p_value, metrics.costes_significant)],
          ['Manders M1 (Red to Green)', formatNumber(metrics.manders_m1, 4), `${formatNumber(metrics.manders_m1 * 100, 1)}% overlap`],
          ['Manders M2 (Green to Red)', formatNumber(metrics.manders_m2, 4), `${formatNumber(metrics.manders_m2 * 100, 1)}% overlap`],
          ['Enrichment (Green at Red)', formatNumber(metrics.enrichment_green_at_R, 2) + 'x', interpretEnrichment(metrics.enrichment_green_at_R)],
          ['Enrichment (Red at Green)', formatNumber(metrics.enrichment_red_at_G, 2) + 'x', interpretEnrichment(metrics.enrichment_red_at_G)],
          ['Red Colocalized', `${formatNumber(metrics.fraction_coloc_red * 100, 1)}%`, `${formatInt(metrics.n_red_fg)} fg voxels`],
          ['Green Colocalized', `${formatNumber(metrics.fraction_coloc_green * 100, 1)}%`, `${formatInt(metrics.n_green_fg)} fg voxels`],
          ['Colocalized Voxels', formatInt(metrics.n_coloc), ''],
        ];

        autoTable(pdf, {
          startY: yPos,
          head: [['Metric', 'Value', 'Interpretation']],
          body: metricsTableData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 9, cellPadding: 2 },
          headStyles: { fillColor: [41, 128, 185], textColor: 255 },
          alternateRowStyles: { fillColor: [245, 245, 245] },
          columnStyles: {
            0: { fontStyle: 'bold', cellWidth: 55 },
            1: { cellWidth: 35, halign: 'center' },
            2: { cellWidth: 55 }
          }
        });

        yPos = pdf.lastAutoTable.finalY + 10;

        // Thresholds
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        pdf.text(`Thresholds - Red: ${formatNumber(metrics.threshold_red, 4)}, Green: ${formatNumber(metrics.threshold_green, 4)}`, margin, yPos);
        yPos += 12;

        // Statistical Significance Section (Costes' Test)
        if (metrics.costes_p_value !== undefined && metrics.costes_p_value !== null) {
          checkPageBreak(35);
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(41, 128, 185);
          pdf.text('Statistical Significance (Costes\' Test)', margin, yPos);
          yPos += 8;

          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(0, 0, 0);
          
          const pValueStr = metrics.costes_p_value < 0.001 ? '< 0.001' : formatNumber(metrics.costes_p_value, 4);
          pdf.text(`p-value: ${pValueStr}`, margin, yPos);
          yPos += 5;
          
          const sigStatus = metrics.costes_significant ? 'SIGNIFICANT (p < 0.05)' : 'Not significant (p >= 0.05)';
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(metrics.costes_significant ? 46 : 150, metrics.costes_significant ? 125 : 150, metrics.costes_significant ? 50 : 150);
          pdf.text(`Result: ${sigStatus}`, margin, yPos);
          yPos += 5;
          
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(100, 100, 100);
          pdf.setFontSize(9);
          const explanation = metrics.costes_significant 
            ? 'The observed colocalization is statistically significant and unlikely due to random chance.'
            : 'The observed colocalization may be due to random overlap and is not statistically significant.';
          pdf.text(explanation, margin, yPos);
          yPos += 12;
        }
      }

      // Van Steensel CCF Section
      if (analysisData.ccf) {
        checkPageBreak(30);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(41, 128, 185);
        pdf.text('Van Steensel Cross-Correlation Function (CCF)', margin, yPos);
        yPos += 8;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        pdf.text(`Best Shift: ${analysisData.ccf.best_shift} pixels`, margin, yPos);
        yPos += 5;
        pdf.text(`Peak Correlation: ${formatNumber(analysisData.ccf.best_correlation, 4)}`, margin, yPos);
        yPos += 5;
        
        const ccfInterpretation = Math.abs(analysisData.ccf.best_shift) <= 1 
          ? 'Channels are well-aligned (no significant shift)' 
          : `Channels show ${analysisData.ccf.best_shift > 0 ? 'positive' : 'negative'} spatial offset`;
        pdf.text(`Interpretation: ${ccfInterpretation}`, margin, yPos);
        yPos += 12;
      }

      // Scatter Plot Statistics
      if (analysisData.scatter) {
        checkPageBreak(25);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(41, 128, 185);
        pdf.text('Intensity Distribution', margin, yPos);
        yPos += 8;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        const sampleCount = analysisData.scatter.red?.length || 0;
        pdf.text(`Sample points analyzed: ${formatInt(sampleCount)}`, margin, yPos);
        yPos += 12;
      }

      // Add captured chart images
      if (chartImages && chartImages.length > 0) {
        pdf.addPage();
        yPos = margin;

        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(41, 128, 185);
        pdf.text('Current Chart View', pageWidth / 2, yPos, { align: 'center' });
        yPos += 15;

        for (const chart of chartImages) {
          checkPageBreak(90);
          
          // Add chart title
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0);
          pdf.text(chart.title, margin, yPos);
          yPos += 5;

          // Calculate dimensions to fit page
          const maxWidth = pageWidth - (margin * 2);
          const maxHeight = 100;
          const ratio = Math.min(maxWidth / chart.width, maxHeight / (chart.height / 2));
          
          const finalWidth = chart.width * ratio;
          const finalHeight = (chart.height / 2) * ratio;

          // Add image to PDF
          pdf.addImage(chart.imgData, 'PNG', margin, yPos, finalWidth, finalHeight);
          yPos += finalHeight + 15;
        }
      }

    } else if (activeView === 'timelapse' && timeLapseData) {
      // Time-lapse specific report
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(41, 128, 185);
      pdf.text('Time-lapse Analysis Summary', margin, yPos);
      yPos += 8;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Total Frames: ${timeLapseData.n_frames}`, margin, yPos);
      yPos += 5;
      pdf.text(`Threshold Strategy: ${timeLapseData.threshold_strategy}`, margin, yPos);
      yPos += 12;

      if (timeLapseData.frame_results && timeLapseData.frame_results.length > 0) {
        // Calculate summary statistics
        const pearsonValues = timeLapseData.frame_results.map(f => f.pearson_r || 0);
        const m1Values = timeLapseData.frame_results.map(f => f.manders_m1 || 0);
        const m2Values = timeLapseData.frame_results.map(f => f.manders_m2 || 0);

        const avgPearson = pearsonValues.reduce((a, b) => a + b, 0) / pearsonValues.length;
        const avgM1 = m1Values.reduce((a, b) => a + b, 0) / m1Values.length;
        const avgM2 = m2Values.reduce((a, b) => a + b, 0) / m2Values.length;

        const minPearson = Math.min(...pearsonValues);
        const maxPearson = Math.max(...pearsonValues);

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Summary Statistics', margin, yPos);
        yPos += 8;

        const summaryData = [
          ['Pearson r (avg)', formatNumber(avgPearson, 4), `Range: ${formatNumber(minPearson, 4)} to ${formatNumber(maxPearson, 4)}`],
          ['Manders M1 (avg)', formatNumber(avgM1, 4), `${formatNumber(avgM1 * 100, 1)}% avg overlap`],
          ['Manders M2 (avg)', formatNumber(avgM2, 4), `${formatNumber(avgM2 * 100, 1)}% avg overlap`],
        ];

        autoTable(pdf, {
          startY: yPos,
          head: [['Metric', 'Average', 'Details']],
          body: summaryData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 9, cellPadding: 2 },
          headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        });

        yPos = pdf.lastAutoTable.finalY + 10;

        // Frame-by-frame data table
        checkPageBreak(50);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Frame-by-Frame Results', margin, yPos);
        yPos += 8;

        const frameData = timeLapseData.frame_results.map((f, i) => [
          i + 1,
          formatNumber(f.pearson_r, 4),
          formatNumber(f.manders_m1, 4),
          formatNumber(f.manders_m2, 4),
          formatInt(f.n_coloc)
        ]);

        autoTable(pdf, {
          startY: yPos,
          head: [['Frame', 'Pearson r', 'M1', 'M2', 'Coloc Pixels']],
          body: frameData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 8, cellPadding: 1.5 },
          headStyles: { fillColor: [41, 128, 185], textColor: 255 },
          alternateRowStyles: { fillColor: [245, 245, 245] },
        });
      }
    }

    // Footer on all pages
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(
        `Coloc3D-STL Report - Page ${i} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    // Generate filename and save
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const timeStr = new Date().toTimeString().slice(0, 8).replace(/:/g, '');
    const filename = `coloc3d_report_${dateStr}_${timeStr}.pdf`;
    
    pdf.save(filename);
    return filename;
  } catch (error) {
    console.error('PDF generation error:', error);
    throw error;
  }
}

// Helper functions
function formatNumber(value, decimals) {
  if (value === undefined || value === null || isNaN(value)) return 'N/A';
  return value.toFixed(decimals);
}

function formatInt(value) {
  if (value === undefined || value === null || isNaN(value)) return 'N/A';
  return value.toLocaleString();
}

function interpretPearson(value) {
  if (value === undefined || value === null || isNaN(value)) return 'N/A';
  if (value > 0.7) return 'Strong positive';
  if (value > 0.4) return 'Moderate positive';
  if (value > 0.1) return 'Weak positive';
  if (value > -0.1) return 'No correlation';
  if (value > -0.4) return 'Weak negative';
  if (value > -0.7) return 'Moderate negative';
  return 'Strong negative';
}

function interpretEnrichment(value) {
  if (value === undefined || value === null || isNaN(value)) return 'N/A';
  if (value > 2) return 'Strong enrichment';
  if (value > 1.2) return 'Moderate enrichment';
  if (value >= 0.8) return 'Random distribution';
  return 'Exclusion';
}

function formatPValue(value) {
  if (value === undefined || value === null || isNaN(value)) return 'N/A';
  if (value < 0.001) return '< 0.001';
  return value.toFixed(4);
}

function interpretCostesSignificance(pValue, isSignificant) {
  if (pValue === undefined || pValue === null) return 'Not calculated';
  if (isSignificant) return 'Significant (p < 0.05)';
  return 'Not significant';
}

export default generateAnalysisReport;
